import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Link,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const theme = createTheme({
  typography: {
    fontFamily: "Inter, Arial",
  },
  palette: {
    primary: {
      main: "#4680FF",
    },
  },
});

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "error",
  });
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // khai báo vị trí này được di chuyển đến nơi khác

  useEffect(() => {
    // Nếu người dùng đã đăng nhập, chuyển hướng đến trang Các dự án
    if (isAuthenticated) {
      navigate("/portal");
    }
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    let valid = true;
    const newErrors = { username: "", password: "" };

    if (!username) {
      newErrors.username = "Tên đăng nhập không được để trống.";
      valid = false;
    }

    if (!password) {
      newErrors.password = "Mật khẩu không được để trống.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      localStorage.removeItem("sidebar-expanded-groups");
      try {
        await login(username, password, rememberMe);
        setAlert({
          show: true,
          message: "Đăng nhập thành công!",
          severity: "success",
        });
        // Chuyển hướng sẽ được xử lý bởi useEffect khi isAuthenticated thay đổi
      } catch (error) {
        console.error("Login error:", error);
        setAlert({
          show: true,
          message:
            error.response?.data?.message ||
            "Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin đăng nhập.",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };
  const handleForgotPassword = (e) => {
    e.preventDefault();
    setAlert({
      show: true,
      message:
        "Vui lòng liên hệ quản trị viên để được hỗ trợ đặt lại mật khẩu.",
      severity: "info",
    });
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        {/* Background wrapper */}
        <Box
          sx={{
            position: "relative",
            minHeight: "100vh",
            backgroundColor: "#f5f5f5",
            overflow: "hidden",
          }}
        >
          {/* Top-left gradient */}
          <Box
            sx={{
              position: "absolute",
              top: "-20%",
              left: "-20%",
              width: "50vw",
              height: "50vw",
              background:
                "radial-gradient(circle at top left, rgba(70,128,255,0.4), transparent 70%)",
              filter: "blur(120px)",
              zIndex: 0,
            }}
          />{" "}
          {/* Middle gradient - Orange */}
          <Box
            sx={{
              position: "absolute",
              left: "30%",
              top: "20%",
              width: "40vw",
              height: "40vw",
              background:
                "radial-gradient(circle at center, rgba(255,140,0,0.35), transparent 70%)",
              filter: "blur(100px)",
              zIndex: 0,
            }}
          />
          {/* Bottom-right gradient */}
          <Box
            sx={{
              position: "absolute",
              bottom: "-20%",
              right: "-20%",
              width: "50vw",
              height: "50vw",
              background:
                "radial-gradient(circle at bottom right, rgba(70,128,255,0.4), transparent 70%)",
              filter: "blur(120px)",
              zIndex: 0,
            }}
          />
          {/* Existing Grid container */}
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: "100vh", position: "relative", zIndex: 1 }}
          >
            <Grid item xs={9} sm={6} md={4} lg={3} xl={2}>
              <Box
                sx={{
                  textAlign: "center",
                  mb: { xs: 2, sm: 3 },
                  mt: { xs: -2, md: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    marginBottom: { xs: "1rem", sm: "2rem", md: "4rem" },
                  }}
                >
                  {/* Logo */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src="/logoXNTD.png"
                      alt="Logo Xí nghiệp Trắc địa"
                      style={{
                        height: "80px",
                        width: "auto",
                      }}
                    />
                  </Box>

                  {/* App Name */}
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#4680FF",
                      fontWeight: 600,
                      fontSize: { xs: "1.8rem", sm: "2rem" },
                      textAlign: { xs: "center", sm: "left" },
                      // marginBottom: { xs: "1rem", sm: "2rem", md: "4rem" },
                    }}
                  >
                    HỆ THỐNG
                    <br />
                    QUẢN LÝ XÍ NGHIỆP TRẮC ĐỊA
                  </Typography>
                </Box>
              </Box>{" "}
              <Box
                sx={{
                  // width: "50%",
                  maxWidth: "400px",
                  backgroundColor: "white",
                  p: { xs: 3, sm: 4 },
                  borderRadius: 2,
                  boxShadow: 0, // Đặt boxShadow về 0 để loại bỏ hiệu ứng nổi
                  mx: "auto", // Căn giữa theo chiều ngang
                  my: { xs: 2, sm: 3 }, // Margin theo chiều dọc
                  border: "1px solid #e0e0e0", // Thêm border màu xám nhạt
                }}
              >
                <Typography
                  variant="h4"
                  align="center"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.2rem", sm: "1.75rem" },
                    color: "#023047",
                    fontWeight: 500,
                  }}
                >
                  Đăng nhập
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Tên đăng nhập"
                    margin="normal"
                    variant="outlined"
                    autoComplete="username"
                    size="medium"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    error={!!errors.username}
                    helperText={errors.username}
                  />
                  <TextField
                    fullWidth
                    label="Mật khẩu"
                    type={showPassword ? "text" : "password"}
                    margin="normal"
                    variant="outlined"
                    autoComplete="current-password"
                    size="medium"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      endAdornment: (
                        <Button
                          onClick={() => setShowPassword((prev) => !prev)}
                          tabIndex={-1}
                          size="small"
                          sx={{ minWidth: 0, px: 1 }}
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </Button>
                      ),
                    }}
                  />
                  {/* <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={2}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 1, sm: 0 }}
                  >
                    {" "}
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                      }
                      label="Ghi nhớ đăng nhập"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        },
                      }}
                    />
                    <Link
                      href="#"
                      underline="hover"
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        alignSelf: { xs: "flex-end", sm: "center" },
                      }}
                      onClick={handleForgotPassword}
                    >
                      Quên mật khẩu?
                    </Link>
                  </Box>{" "} */}
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      mt: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      boxShadow: 0, // Đặt boxShadow về 0 để loại bỏ hiệu ứng nổi cho nút
                      "&:hover": {
                        boxShadow: 0, // Đảm bảo khi hover cũng không có hiệu ứng nổi
                      },
                    }}
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? <CircularProgress size={24} /> : "Đăng nhập"}
                  </Button>
                </Box>
                <Typography
                  align="center"
                  mt={2}
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Bạn chưa có tài khoản?{" "}
                  <Link href="/register" underline="hover">
                    Đăng ký
                  </Link>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </ThemeProvider>{" "}
      <Snackbar
        open={alert.show}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, show: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, show: false })}
          severity={alert.severity}
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            color:
              alert.severity === "info"
                ? "orange"
                : alert.severity === "error"
                ? "error.main"
                : alert.severity === "success"
                ? "success.main"
                : "text.primary",
            borderColor:
              alert.severity === "info"
                ? "orange"
                : alert.severity === "error"
                ? "error.light"
                : alert.severity === "success"
                ? "success.light"
                : "text.secondary",
            "& .MuiAlert-icon": {
              color: alert.severity === "info" ? "orange" : undefined,
            },
          }}
          variant="outlined"
        >
          {alert.message}
        </Alert>
      </Snackbar>
      {/* App description bottom */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100%",
          textAlign: "center",
          pb: 2,
          zIndex: 10,
        }}
      >
        <Typography
          sx={{ color: "#515151ff", fontSize: "0.95rem", fontWeight: 600 }}
        >
          © {new Date().getFullYear()} Tổ Nghiên cứu và Phát triển - Xí nghiệp
          Trắc địa
        </Typography>
      </Box>
    </>
  );
}

export default LoginPage;
